import { lazy, Suspense } from "react";

/* Same lazy imports as the parent — keeping them here means the modal chunks
 * load on first open from THIS module, not from ELPMStudioDAW. Either entry
 * point hits the same Webpack chunk so cache hits cross between them. */
const LyricsWriter          = lazy(() => import("@/components/LyricsWriter"));
const SongCreator           = lazy(() => import("@/components/SongCreator"));
const VoiceClone            = lazy(() => import("@/components/VoiceClone"));
const SunoModal             = lazy(() => import("@/components/SunoModal"));
const LoopStationModal      = lazy(() => import("@/components/LoopStationModal"));
const SongLibrary           = lazy(() => import("@/components/SongLibrary"));
const MidiBridgeModal       = lazy(() => import("@/components/MidiBridgeModal"));
const DrumMachineModal      = lazy(() => import("@/components/DrumMachineModal"));
const BassPlayerModal       = lazy(() => import("@/components/BassPlayerModal"));
const JSFXEditorModal       = lazy(() => import("@/components/JSFXEditorModal"));
const BackingVocalsModal    = lazy(() => import("@/components/BackingVocalsModal"));
const ClipEditorModal       = lazy(() => import("@/components/ClipEditorModal"));
const MultiTrackEditorModal = lazy(() => import("@/components/MultiTrackEditorModal"));
const LoopLibraryModal      = lazy(() => import("@/components/LoopLibraryModal"));
const JamWizardModal        = lazy(() => import("@/components/JamWizardModal"));
const RecordingsModal       = lazy(() => import("@/components/RecordingsModal"));
const ShareSessionModal     = lazy(() => import("@/components/ShareSessionModal"));

/**
 * DawModals — declaratively renders every lazy-loaded modal under a single
 * Suspense boundary. Lifted out of ELPMStudioDAW.jsx during the iter-30
 * refactor so the parent component is easier to read; behaviour is unchanged.
 *
 * Open/close state remains in the parent; this component is purely the JSX
 * "render the modal tree" responsibility. All callbacks are passed through
 * verbatim — there is no behavioural change to any flow.
 */
export default function DawModals({
  engine,
  channels,
  selectedChannel,
  playhead,
  setSelectedChannel,
  setMaxDur,
  setLoadedCount,
  setPlaying,
  setTempo,
  setSunoSeed,
  setSongSeed,
  setSingCtaInfo,
  // Open/close flags
  lyricsOpen, setLyricsOpen,
  songOpen, setSongOpen, songSeed,
  voiceOpen, setVoiceOpen,
  libraryOpen, setLibraryOpen,
  midiOpen, setMidiOpen,
  armedChannels, onChannelArm,
  drumOpen, setDrumOpen,
  bassOpen, setBassOpen,
  jsfxOpen, setJsfxOpen,
  backingVocalsOpen, setBackingVocalsOpen,
  clipOpen, setClipOpen,
  mtEditorOpen, setMtEditorOpen,
  loopsOpen, setLoopsOpen,
  jamWizardOpen, setJamWizardOpen,
  sunoOpen, setSunoOpen, sunoSeed,
  loopStationOpen, setLoopStationOpen,
  recordingsOpen, setRecordingsOpen,
  shareSessionOpen, setShareSessionOpen,
  tempo, musicalKey,
}) {
  return (
    <Suspense fallback={null}>
      <LyricsWriter
        open={lyricsOpen}
        onClose={() => setLyricsOpen(false)}
        initialTitle={channels[selectedChannel]?.name ? channels[selectedChannel].name.replace(/\.[^.]+$/, "") : ""}
        engine={engine}
        channels={channels}
        playhead={playhead}
        onMakeSong={(prompt, lyrics) => {
          setLyricsOpen(false);
          setSongSeed({ prompt: prompt || "", lyrics: lyrics || "" });
          setSongOpen(true);
        }}
        onSendToSuno={({ lyrics, style, title }) => {
          setLyricsOpen(false);
          setSunoSeed({ lyrics, style, title });
          setSunoOpen(true);
        }}
      />
      <SongCreator
        open={songOpen}
        onClose={() => setSongOpen(false)}
        engine={engine}
        defaultPrompt={songSeed.prompt}
        defaultLyrics={songSeed.lyrics}
        onPlayStarted={(meta) => {
          setPlaying(true);
          setMaxDur(engine.maxDuration);
          setLoadedCount(engine.channels.filter((c) => c.buffer).length);
          // Genre kits set their own native tempo (Trap=140, Reggae=76, etc.) —
          // reflect that in the React state so the Tempo pill updates.
          if (meta?.tempo) {
            setTempo(meta.tempo);
            try { localStorage.setItem("elpm-tempo", String(meta.tempo)); } catch (e) { void e; }
          }
        }}
        onChannelsLoaded={() => {
          // iter-275 — channels gained audio without playback starting
          // (Suno compose fallback / stem extraction). Sync transport
          // state so PLAY is no longer gated by loadedCount === 0.
          setMaxDur(engine.maxDuration);
          setLoadedCount(engine.channels.filter((c) => c.buffer).length);
        }}
        onPlaybackPaused={() => setPlaying(false)}
      />
      <VoiceClone
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        engine={engine}
        channels={channels}
        selectedChannel={selectedChannel}
        onChannelLoaded={(idx) => {
          setSelectedChannel(idx);
          setMaxDur(engine.maxDuration);
          setLoadedCount(engine.channels.filter((c) => c.buffer).length);
        }}
      />
      <SongLibrary
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        engine={engine}
        onSongLoaded={(idx) => {
          setSelectedChannel(idx);
          setMaxDur(engine.maxDuration);
          setLoadedCount(engine.channels.filter((c) => c.buffer).length);
        }}
        onRemix={(song) => {
          // "Remix" — pump lyrics + style + title into Suno custom-lyrics mode
          // so the user can tweak any of them and regenerate a new take.
          //
          // iter-83: Suno's customMode REQUIRES style + title. If the source
          // library song lacks them, auto-derive sensible defaults from the
          // original prompt + filename so Generate doesn't immediately fail
          // with a confusing "[400] missing style" error. User can still
          // edit either field before clicking Generate.
          const fallbackStyle = (song.style || song.prompt || "soulful, warm, full-band")
            .replace(/\s+/g, " ")
            .trim()
            .slice(0, 120);
          const fallbackTitle = (song.title || "Remixed song").slice(0, 80);
          setSunoSeed({
            kind: "remix",
            lyrics: song.lyrics || "",
            // Fall back to the original prompt when no lyrics were saved.
            prompt: song.lyrics ? "" : (song.prompt || ""),
            style: fallbackStyle,
            title: fallbackTitle,
          });
          setSunoOpen(true);
        }}
        onReusePrompt={(song) => {
          // "Reuse Prompt" — load just the prompt (free-form vibe), no custom lyrics.
          setSunoSeed({
            kind: "reuse",
            prompt: song.prompt || "",
            style: song.style || "",
            title: song.title || "",
          });
          setSunoOpen(true);
        }}
        /* Lets the per-card empty-state CTA open the Voice Clone modal when
           a user wants to Re-sing a library song but hasn't trained any
           voices yet. Library stays mounted so they can come back. */
        onOpenVoiceClone={() => setVoiceOpen(true)}
      />
      <MidiBridgeModal
        open={midiOpen}
        onClose={() => setMidiOpen(false)}
        engine={engine}
        armedChannels={armedChannels}
        onChannelArm={onChannelArm}
      />
      <DrumMachineModal
        open={drumOpen}
        onClose={() => setDrumOpen(false)}
        engine={engine}
        onLoaded={() => {
          setPlaying(true);
          setMaxDur(engine.maxDuration);
          setLoadedCount(engine.channels.filter((c) => c.buffer).length);
          setTempo(engine.tempo);
        }}
      />
      <BassPlayerModal
        open={bassOpen}
        onClose={() => setBassOpen(false)}
        engine={engine}
        onLoaded={() => {
          setPlaying(true);
          setMaxDur(engine.maxDuration);
          setLoadedCount(engine.channels.filter((c) => c.buffer).length);
          setTempo(engine.tempo);
        }}
      />
      {/* iter-144 — JSFX Editor (REAPER-style text DSP, offline buffer render). */}
      <JSFXEditorModal
        open={jsfxOpen}
        onClose={() => setJsfxOpen(false)}
        engine={engine}
      />
      {/* iter-111 — Backing Vocals (stacked AI harmonies). Lands as one
          mixed stereo stem on the next free channel. */}
      <BackingVocalsModal
        open={backingVocalsOpen}
        onClose={() => setBackingVocalsOpen(false)}
        engine={engine}
      />
      <LoopLibraryModal
        open={loopsOpen}
        onClose={() => setLoopsOpen(false)}
        engine={engine}
        onLoaded={() => {
          setPlaying(true);
          setMaxDur(engine.maxDuration);
          setLoadedCount(engine.channels.filter((c) => c.buffer).length);
          setTempo(engine.tempo);
        }}
      />
      <JamWizardModal
        open={jamWizardOpen}
        onClose={() => setJamWizardOpen(false)}
        engine={engine}
        onComplete={(meta) => {
          setPlaying(true);
          setMaxDur(engine.maxDuration);
          setLoadedCount(engine.channels.filter((c) => c.buffer).length);
          if (meta?.tempo) setTempo(meta.tempo);
          // Show "Sing over it" CTA only if no vocals were added.
          if (!meta?.channels?.vocals?.length) {
            setSingCtaInfo({ genre: meta.genre, tempo: meta.tempo });
          }
        }}
      />
      <SunoModal
        open={sunoOpen}
        onClose={() => { setSunoOpen(false); setSunoSeed(null); }}
        engine={engine}
        seed={sunoSeed}
        onSongLoaded={(ch) => {
          setMaxDur(engine.maxDuration);
          setLoadedCount(engine.channels.filter((c) => c.buffer).length);
          setSelectedChannel(ch);
        }}
        /* Lets the empty-state CTA ("Want to re-sing this in your own voice?")
           open the Voice Clone modal in one click. Make Music stays open
           underneath so the user can come back and finish the Re-sing flow. */
        onOpenVoiceClone={() => setVoiceOpen(true)}
      />
      <LoopStationModal
        open={loopStationOpen}
        onClose={() => setLoopStationOpen(false)}
        engine={engine}
        onLoopsSentToMixer={(channels) => {
          // Update the studio's maxDur / loaded count after the looper drops
          // its takes into the mixer. Same pattern Suno/Lyrics/Song use.
          setMaxDur(engine.maxDuration);
          setLoadedCount(engine.channels.filter((c) => c.buffer).length);
          if (channels && channels.length) setSelectedChannel(channels[0]);
        }}
      />
      <ClipEditorModal
        open={clipOpen}
        onClose={() => setClipOpen(false)}
        engine={engine}
        channelIdx={selectedChannel}
        onChange={() => {
          setMaxDur(engine.maxDuration);
          setLoadedCount(engine.channels.filter((c) => c.buffer).length);
        }}
      />
      <MultiTrackEditorModal
        open={mtEditorOpen}
        onClose={() => setMtEditorOpen(false)}
        engine={engine}
        onChange={() => {
          setMaxDur(engine.maxDuration);
          setLoadedCount(engine.channels.filter((c) => c.buffer).length);
        }}
      />
      <RecordingsModal
        open={recordingsOpen}
        onClose={() => setRecordingsOpen(false)}
        engine={engine}
        onChannelLoaded={(ch) => {
          setMaxDur(engine.maxDuration);
          setLoadedCount(engine.channels.filter((c) => c.buffer).length);
          setSelectedChannel(ch);
        }}
      />
      <ShareSessionModal
        open={shareSessionOpen}
        onClose={() => setShareSessionOpen(false)}
        engine={engine}
        tempo={tempo}
        musicalKey={musicalKey}
        lyrics=""
      />
    </Suspense>
  );
}
